import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { HttpRequestStatus, LoadingContainer } from "./loadingContainer";
import useReportRows from "../hooks/useReportRows";
import { Report, ReportRow } from "../api/models/report.models";
import useEcomodClasses from "../hooks/useEcomodClasses";
import { isInteger, ReusableClass } from "../utils/rowUtils";
import { TabView, TabPanel } from "primereact/tabview";
import { getSecondaryClassLabelFromAkeneoObject } from "../utils/akeneoUtils";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import styled from "styled-components";
import { useForm } from "react-hook-form";
import { RowValuesWithEcomodClass } from "../api/models/rowValues.models";
import MaterialTab from "./materialTab";
import {
  MaterialFormDataWithSpecificCodes,
  SecondaryMaterials,
} from "../types/materialForm.types";
import {
  CreateReportRowPayload,
  UpdateReportRowPayload,
} from "../api/models/reportRow.models";
import ReportFooter from "./reportFooter";
import { EcomodClass } from "../api/models/ecomodClass.models";
import { useMaterials } from "../hooks/useMaterials";
import { Button } from "primereact/button";
import EmptyCupTab from "./emptyCupTab";
import { InputInstructionsHover } from "./inputInstructionsHover";

const PaddedContainer = styled.div`
  padding: 2rem;
  min-height: 100%;
`;

const RelativeTabView = styled(TabView)`
  position: relative;

  & .p-tabview-nav-container {
    position: sticky;
    top: 0;
    /* Must beat other fixed/sticky chrome (site header, chatbot, etc.) since this
       creates its own stacking context that caps the tooltip's z-index inside it */
    z-index: 1200;
    border-bottom: 1px solid #ccc;
    background-color: #fff;
  }

  /* PrimeReact clips these with overflow: hidden, which would cut off the tab tooltip */
  & .p-tabview-nav-content,
  & .p-tabview-nav-link {
    overflow: visible;
  }

  /* Style tabs with errors */
  & .tab-with-error {
    border-bottom: 2px solid #dc3545 !important;
    color: #dc3545 !important;
  }

  & .p-tabview-nav li:has(.tab-with-error) {
    border-bottom: 2px solid #dc3545;
  }
`;

const TabHeaderWithError = styled.span<{ $hasError: boolean }>`
  ${(props) =>
    props.$hasError &&
    `
    color: var(--sumi-error-color);
    border-bottom: 2px solid var(--sumi-error-color);
  `}
`;

interface ReportFormProps {
  reportId?: number;
  reportData?: Report;
  isLoadingReport: boolean;
  handleNextStep: () => void;
  onFormDirtyChange?: (isDirty: boolean) => void;
  saveDraftRef?: React.MutableRefObject<(() => Promise<void>) | undefined>;
}

const BasicReportForm: React.FC<ReportFormProps> = ({
  reportId,
  isLoadingReport,
  reportData,
  handleNextStep,
  onFormDirtyChange,
  saveDraftRef,
}: ReportFormProps) => {
  const { t } = useTranslation();
  const [locale, toast] = useUIStore(
    useShallow((state: UIState) => [state.locale, state.toastRef]),
  );
  const [tabs, setTabs] = React.useState<
    { code: string; label: string; tooltip: string }[]
  >([]);
  const [activeTabIndex, setActiveTabIndex] = React.useState<number>(0);
  const [validationErrorFields, setValidationErrorFields] = React.useState<
    { fieldPath: string; message: string }[]
  >([]);

  // Helper function to check if a tab has validation errors
  const tabHasErrors = (tabCode: string) => {
    const isEmptyCupTab = tabCode === "x_1" || tabCode === "x_2";
    if (isEmptyCupTab) {
      // For empty cup tabs, check for cup-related errors
      const cupPattern = isEmptyCupTab
        ? tabCode === "x_1"
          ? "Cups"
          : "Cups"
        : "";
      return validationErrorFields.some(({ fieldPath }) =>
        fieldPath.includes(cupPattern),
      );
    } else {
      // For regular material tabs, check if any error field starts with this tab code
      return validationErrorFields.some(({ fieldPath }) => {
        // Extract the material code from the error field path
        const pathParts = fieldPath.split(".");
        const lastPart = pathParts[pathParts.length - 1]; // This should be the material code
        return lastPart.startsWith(tabCode);
      });
    }
  };

  const { ecomodClasses, isLoading: isLoadingEcomodClasses } =
    useEcomodClasses();

  const { multiMaterialPackageCodes, businessOnlyMultimaterialPackageCodes } =
    useMaterials();

  const {
    createReportRowData,
    isCreatingReportRow,
    updateReportRowData,
    isUpdatingReportRow,
  } = useReportRows();

  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
    watch,
    reset,
    getValues,
  } = useForm<MaterialFormDataWithSpecificCodes>({
    mode: "onBlur",
    defaultValues: {
      consumerPackaging: {
        singleUse: {
          packagedInFinland: {},
          importedPackages: {},
          multiMaterial: {
            packagedInFinland: {},
            importedPackages: {},
          },
        },
        reusable: {
          firstUsage: {},
          importedPackages: {},
          reused: {},
          multiMaterial: {
            firstUsage: {},
            importedPackages: {},
            reused: {},
          },
        },
        secondaryMaterials: [],
      },
      businessPackaging: {
        singleUse: {
          packagedInFinland: {},
          importedPackages: {},
          multiMaterial: {
            packagedInFinland: {},
            importedPackages: {},
          },
        },
        reusable: {
          firstUsage: {},
          importedPackages: {},
          reused: {},
          multiMaterial: {
            firstUsage: {},
            importedPackages: {},
            reused: {},
          },
        },
        secondaryMaterials: [],
      },
    },
  });

  useEffect(() => {
    // Notify parent of form dirty state changes
    if (onFormDirtyChange) {
      onFormDirtyChange(isDirty);
    }
  }, [isDirty, onFormDirtyChange]);

  useEffect(() => {
    // update formdata ONLY if user has not made changes
    if (!isDirty && reportData?.rows && reportData.rows.length > 0) {
      // Convert report row values back to form data structure
      const formData: MaterialFormDataWithSpecificCodes = {
        consumerPackaging: {
          singleUse: {
            packagedInFinland: {},
            importedPackages: {},
            multiMaterial: {
              packagedInFinland: {},
              importedPackages: {},
            },
          },
          reusable: {
            firstUsage: {},
            importedPackages: {},
            reused: {},
            multiMaterial: {
              firstUsage: {},
              importedPackages: {},
              reused: {},
            },
          },
          secondaryMaterials: [],
        },
        businessPackaging: {
          singleUse: {
            packagedInFinland: {},
            importedPackages: {},
            multiMaterial: {
              packagedInFinland: {},
              importedPackages: {},
            },
          },
          reusable: {
            firstUsage: {},
            importedPackages: {},
            reused: {},
            multiMaterial: {
              firstUsage: {},
              importedPackages: {},
              reused: {},
            },
          },
          secondaryMaterials: [],
        },
        consumerCups: {
          producedInFinland: {},
          importedCups: {},
          multiMaterial: {
            producedInFinland: {},
            importedCups: {},
          },
          secondaryMaterials: [],
        },
        businessCups: {
          producedInFinland: {},
          importedCups: {},
          multiMaterial: {
            producedInFinland: {},
            importedCups: {},
          },
          secondaryMaterials: [],
        },
      };

      reportData.rows.forEach((row) => {
        row.values?.forEach((value) => {
          if (value.supClass) {
            return; // Skip supClass entries - they have their own form
          }

          // Determine the path based on businessUse, reusableClass, and packagedAbroad
          const isCup = value.isEmptyCup;
          const isConsumer = !value.businessUse;
          const isReusable = value.reusableClass !== ReusableClass.NONE;
          const isImported = value.packagedAbroad;
          const isReused = value.reusableClass === ReusableClass.REUSABLE;
          const isMultiMaterial =
            multiMaterialPackageCodes.includes(
              value.ecoModulationClass?.code || "",
            ) ||
            (isConsumer === false &&
              businessOnlyMultimaterialPackageCodes.includes(
                value.ecoModulationClass?.code || "",
              ));

          if (value.ecoModulationClass?.code && !value.secMaterial) {
            const materialCode = value.ecoModulationClass.code;
            const amount = value.value?.toString() || "";

            // Primary materials
            if (isConsumer) {
              if (isCup) {
                if (!formData.consumerCups) formData.consumerCups = {} as any;

                if (isMultiMaterial) {
                  // Handle multimaterial cups
                  if (!formData.consumerCups.multiMaterial)
                    formData.consumerCups.multiMaterial = {} as any;

                  if (isImported) {
                    if (!formData.consumerCups.multiMaterial.importedCups)
                      formData.consumerCups.multiMaterial.importedCups = {};
                    formData.consumerCups.multiMaterial.importedCups[
                      materialCode
                    ] = amount;
                  } else {
                    if (!formData.consumerCups.multiMaterial.producedInFinland)
                      formData.consumerCups.multiMaterial.producedInFinland =
                        {};
                    formData.consumerCups.multiMaterial.producedInFinland[
                      materialCode
                    ] = amount;
                  }
                } else {
                  // Handle single material cups
                  if (isImported) {
                    if (!formData.consumerCups.importedCups)
                      formData.consumerCups.importedCups = {};
                    formData.consumerCups.importedCups[materialCode] = amount;
                  } else {
                    if (!formData.consumerCups.producedInFinland)
                      formData.consumerCups.producedInFinland = {};
                    formData.consumerCups.producedInFinland[materialCode] =
                      amount;
                  }
                }
                return;
              }
              if (!formData.consumerPackaging)
                formData.consumerPackaging = {} as any;

              if (isReusable) {
                if (!formData.consumerPackaging.reusable)
                  formData.consumerPackaging.reusable = {} as any;

                if (isMultiMaterial) {
                  // Handle multiMaterial reusable
                  if (!formData.consumerPackaging.reusable.multiMaterial)
                    formData.consumerPackaging.reusable.multiMaterial =
                      {} as any;

                  if (isReused) {
                    if (
                      !formData.consumerPackaging.reusable.multiMaterial.reused
                    )
                      formData.consumerPackaging.reusable.multiMaterial.reused =
                        {};
                    formData.consumerPackaging.reusable.multiMaterial.reused[
                      materialCode
                    ] = amount;
                  } else if (isImported) {
                    if (
                      !formData.consumerPackaging.reusable.multiMaterial
                        .importedPackages
                    )
                      formData.consumerPackaging.reusable.multiMaterial.importedPackages =
                        {};
                    formData.consumerPackaging.reusable.multiMaterial.importedPackages[
                      materialCode
                    ] = amount;
                  } else {
                    if (
                      !formData.consumerPackaging.reusable.multiMaterial
                        .firstUsage
                    )
                      formData.consumerPackaging.reusable.multiMaterial.firstUsage =
                        {};
                    formData.consumerPackaging.reusable.multiMaterial.firstUsage[
                      materialCode
                    ] = amount;
                  }
                } else {
                  // Handle regular reusable
                  if (isReused) {
                    if (!formData.consumerPackaging.reusable.reused)
                      formData.consumerPackaging.reusable.reused = {};
                    formData.consumerPackaging.reusable.reused[materialCode] =
                      amount;
                  } else if (isImported) {
                    if (!formData.consumerPackaging.reusable.importedPackages)
                      formData.consumerPackaging.reusable.importedPackages = {};
                    formData.consumerPackaging.reusable.importedPackages[
                      materialCode
                    ] = amount;
                  } else {
                    if (!formData.consumerPackaging.reusable.firstUsage)
                      formData.consumerPackaging.reusable.firstUsage = {};
                    formData.consumerPackaging.reusable.firstUsage[
                      materialCode
                    ] = amount;
                  }
                }
              } else {
                if (!formData.consumerPackaging.singleUse)
                  formData.consumerPackaging.singleUse = {} as any;

                if (isMultiMaterial) {
                  // Handle multiMaterial singleUse
                  if (!formData.consumerPackaging.singleUse.multiMaterial)
                    formData.consumerPackaging.singleUse.multiMaterial =
                      {} as any;

                  if (isImported) {
                    if (
                      !formData.consumerPackaging.singleUse.multiMaterial
                        .importedPackages
                    )
                      formData.consumerPackaging.singleUse.multiMaterial.importedPackages =
                        {};
                    formData.consumerPackaging.singleUse.multiMaterial.importedPackages[
                      materialCode
                    ] = amount;
                  } else {
                    if (
                      !formData.consumerPackaging.singleUse.multiMaterial
                        .packagedInFinland
                    )
                      formData.consumerPackaging.singleUse.multiMaterial.packagedInFinland =
                        {};
                    formData.consumerPackaging.singleUse.multiMaterial.packagedInFinland[
                      materialCode
                    ] = amount;
                  }
                } else {
                  // Handle regular singleUse
                  if (isImported) {
                    if (!formData.consumerPackaging.singleUse.importedPackages)
                      formData.consumerPackaging.singleUse.importedPackages =
                        {};
                    formData.consumerPackaging.singleUse.importedPackages[
                      materialCode
                    ] = amount;
                  } else {
                    if (!formData.consumerPackaging.singleUse.packagedInFinland)
                      formData.consumerPackaging.singleUse.packagedInFinland =
                        {};
                    formData.consumerPackaging.singleUse.packagedInFinland[
                      materialCode
                    ] = amount;
                  }
                }
              }
            } else {
              if (!formData.businessPackaging)
                formData.businessPackaging = {} as any;

              if (isCup) {
                if (!formData.businessCups) formData.businessCups = {} as any;

                if (isMultiMaterial) {
                  // Handle multimaterial cups
                  if (!formData.businessCups.multiMaterial)
                    formData.businessCups.multiMaterial = {} as any;

                  if (isImported) {
                    if (!formData.businessCups.multiMaterial.importedCups)
                      formData.businessCups.multiMaterial.importedCups = {};
                    formData.businessCups.multiMaterial.importedCups[
                      materialCode
                    ] = amount;
                  } else {
                    if (!formData.businessCups.multiMaterial.producedInFinland)
                      formData.businessCups.multiMaterial.producedInFinland =
                        {};
                    formData.businessCups.multiMaterial.producedInFinland[
                      materialCode
                    ] = amount;
                  }
                } else {
                  // Handle single material cups
                  if (isImported) {
                    if (!formData.businessCups.importedCups)
                      formData.businessCups.importedCups = {};
                    formData.businessCups.importedCups[materialCode] = amount;
                  } else {
                    if (!formData.businessCups.producedInFinland)
                      formData.businessCups.producedInFinland = {};
                    formData.businessCups.producedInFinland[materialCode] =
                      amount;
                  }
                }
                return;
              }

              if (isReusable) {
                if (!formData.businessPackaging.reusable)
                  formData.businessPackaging.reusable = {} as any;

                if (isMultiMaterial) {
                  // Handle multiMaterial reusable
                  if (!formData.businessPackaging.reusable.multiMaterial)
                    formData.businessPackaging.reusable.multiMaterial =
                      {} as any;

                  if (isReused) {
                    if (
                      !formData.businessPackaging.reusable.multiMaterial.reused
                    )
                      formData.businessPackaging.reusable.multiMaterial.reused =
                        {};
                    formData.businessPackaging.reusable.multiMaterial.reused[
                      materialCode
                    ] = amount;
                  } else if (isImported) {
                    if (
                      !formData.businessPackaging.reusable.multiMaterial
                        .importedPackages
                    )
                      formData.businessPackaging.reusable.multiMaterial.importedPackages =
                        {};
                    formData.businessPackaging.reusable.multiMaterial.importedPackages[
                      materialCode
                    ] = amount;
                  } else {
                    if (
                      !formData.businessPackaging.reusable.multiMaterial
                        .firstUsage
                    )
                      formData.businessPackaging.reusable.multiMaterial.firstUsage =
                        {};
                    formData.businessPackaging.reusable.multiMaterial.firstUsage[
                      materialCode
                    ] = amount;
                  }
                } else {
                  // Handle regular reusable
                  if (isReused) {
                    if (!formData.businessPackaging.reusable.reused)
                      formData.businessPackaging.reusable.reused = {};
                    formData.businessPackaging.reusable.reused[materialCode] =
                      amount;
                  } else if (isImported) {
                    if (!formData.businessPackaging.reusable.importedPackages)
                      formData.businessPackaging.reusable.importedPackages = {};
                    formData.businessPackaging.reusable.importedPackages[
                      materialCode
                    ] = amount;
                  } else {
                    if (!formData.businessPackaging.reusable.firstUsage)
                      formData.businessPackaging.reusable.firstUsage = {};
                    formData.businessPackaging.reusable.firstUsage[
                      materialCode
                    ] = amount;
                  }
                }
              } else {
                if (!formData.businessPackaging.singleUse)
                  formData.businessPackaging.singleUse = {} as any;

                if (isMultiMaterial) {
                  // Handle multiMaterial singleUse
                  if (!formData.businessPackaging.singleUse.multiMaterial)
                    formData.businessPackaging.singleUse.multiMaterial =
                      {} as any;

                  if (isImported) {
                    if (
                      !formData.businessPackaging.singleUse.multiMaterial
                        .importedPackages
                    )
                      formData.businessPackaging.singleUse.multiMaterial.importedPackages =
                        {};
                    formData.businessPackaging.singleUse.multiMaterial.importedPackages[
                      materialCode
                    ] = amount;
                  } else {
                    if (
                      !formData.businessPackaging.singleUse.multiMaterial
                        .packagedInFinland
                    )
                      formData.businessPackaging.singleUse.multiMaterial.packagedInFinland =
                        {};
                    formData.businessPackaging.singleUse.multiMaterial.packagedInFinland[
                      materialCode
                    ] = amount;
                  }
                } else {
                  // Handle regular singleUse
                  if (isImported) {
                    if (!formData.businessPackaging.singleUse.importedPackages)
                      formData.businessPackaging.singleUse.importedPackages =
                        {};
                    formData.businessPackaging.singleUse.importedPackages[
                      materialCode
                    ] = amount;
                  } else {
                    if (!formData.businessPackaging.singleUse.packagedInFinland)
                      formData.businessPackaging.singleUse.packagedInFinland =
                        {};
                    formData.businessPackaging.singleUse.packagedInFinland[
                      materialCode
                    ] = amount;
                  }
                }
              }
            }
          } else if (value.secMaterial?.code) {
            if (value.secMaterial && value.secValue && value.secValueType) {
              // It's a secondary material
              const amount = value.secValue.toString();

              if (isCup) {
                // Handle cups secondary materials
                const targetArray = isConsumer
                  ? formData.consumerCups.secondaryMaterials
                  : formData.businessCups.secondaryMaterials;

                // Find existing entry or create a new one
                let secMaterialEntry = targetArray?.find(
                  (entry) =>
                    entry.materialCode === value.secMaterial?.code &&
                    entry.ecoModulationClassCode ===
                      value.ecoModulationClass?.code,
                );

                if (!secMaterialEntry) {
                  secMaterialEntry = {
                    materialCode: value.secMaterial.code,
                    ecoModulationClassCode:
                      value.ecoModulationClass?.code || "",
                    producedInFinland: "",
                    importedCups: "",
                  };
                  targetArray?.push(secMaterialEntry);
                }

                // Update the appropriate field
                if (isImported) {
                  secMaterialEntry.importedCups = amount;
                } else {
                  secMaterialEntry.producedInFinland = amount;
                }
              } else {
                // Handle packaging secondary materials
                const targetArray = isConsumer
                  ? formData.consumerPackaging.secondaryMaterials
                  : formData.businessPackaging.secondaryMaterials;

                // Find existing entry or create a new one
                let secMaterialEntry = targetArray?.find(
                  (entry) =>
                    entry.materialCode === value.secMaterial?.code &&
                    entry.ecoModulationClassCode ===
                      value.ecoModulationClass?.code,
                );

                if (!secMaterialEntry) {
                  secMaterialEntry = {
                    materialCode: value.secMaterial.code,
                    ecoModulationClassCode:
                      value.ecoModulationClass?.code || "",
                    singleUse: {
                      packagedInFinland: "",
                      importedPackages: "",
                    },
                    reusable: {
                      firstUsage: "",
                      importedPackages: "",
                      reused: "",
                    },
                  };
                  targetArray?.push(secMaterialEntry);
                }

                // Update the appropriate field
                if (isReusable) {
                  // Handle multiMaterial reusable
                  if (isReused) {
                    secMaterialEntry.reusable.reused = amount;
                  } else if (isImported) {
                    secMaterialEntry.reusable.importedPackages = amount;
                  } else {
                    secMaterialEntry.reusable.firstUsage = amount;
                  }
                } else {
                  // Handle multiMaterial singleUse
                  if (isImported) {
                    secMaterialEntry.singleUse.importedPackages = amount;
                  } else {
                    secMaterialEntry.singleUse.packagedInFinland = amount;
                  }
                }
              }

              return; // Skip the rest of the loop for secondary materials
            }
          }
        });
      });

      // Reset form with the populated data
      reset(formData);
    }
  }, [reportData]);

  const isLoading = isLoadingReport || isLoadingEcomodClasses;

  const findMatchingIdFromReportData = (
    reportData: Report | undefined,
    materialCode: string,
    businessUse: boolean,
    reusableClass: ReusableClass,
    packagedAbroad: boolean,
    isCup?: boolean,
  ): number | undefined => {
    if (!reportData) return undefined;

    for (const row of reportData.rows) {
      for (const value of row.values || []) {
        if (
          value.ecoModulationClass?.code === materialCode &&
          value.businessUse === businessUse &&
          value.reusableClass === reusableClass &&
          value.packagedAbroad === packagedAbroad &&
          value.isEmptyCup === isCup &&
          !value.secValue &&
          !value.secMaterial
        ) {
          return value.id;
        }
      }
    }
    return undefined;
  };

  const findMatchingSecondaryIdFromReportData = (
    reportData: Report | undefined,
    materialCode: string,
    linkedEcomodClassCode: string,
    businessUse: boolean,
    reusableClass: ReusableClass,
    packagedAbroad: boolean,
    isEmptyCup: boolean = false,
  ): number | undefined => {
    if (!reportData) return undefined;

    for (const row of reportData.rows) {
      for (const value of row.values || []) {
        if (
          value.ecoModulationClass?.code === linkedEcomodClassCode &&
          value.secMaterial?.code === materialCode &&
          value.businessUse === businessUse &&
          value.reusableClass === reusableClass &&
          value.packagedAbroad === packagedAbroad &&
          value.isEmptyCup === isEmptyCup
        ) {
          return value.id;
        }
      }
    }
    return undefined;
  };

  const generateRowFromData = (
    data: MaterialFormDataWithSpecificCodes,
  ): RowValuesWithEcomodClass[] => {
    let rows: RowValuesWithEcomodClass[] = [];

    // Helper function to process material class data
    const processMaterialData = (
      materialData: { [materialCode: string]: string },
      businessUse: boolean,
      reusableClass: ReusableClass,
      packagedAbroad: boolean,
      isEmptyCup: boolean = false,
    ) => {
      Object.entries(materialData).forEach(([materialCode, value]) => {
        if (value && parseFloat(value) > 0) {
          // Find the corresponding ecomod class
          const ecomodClass = ecomodClasses?.find(
            (cls) => cls.code === materialCode,
          );

          rows.push({
            id: findMatchingIdFromReportData(
              reportData,
              materialCode,
              businessUse,
              reusableClass,
              packagedAbroad,
              isEmptyCup,
            ),
            ecoModulationClass: ecomodClass,
            businessUse,
            reusableClass,
            packagedAbroad,
            value: parseFloat(value),
            secMaterial: null,
            secValue: 0,
            secValueType: undefined,
            isEmptyCup: isEmptyCup,
          });
        }
      });
    };

    const processSecondaryMaterials = (
      materialCode: string, // Code for secondary material , for example 1 for PAPER
      businessUse: boolean,
      reusableClass: ReusableClass,
      packagedAbroad: boolean,
      value: number,
      ownerEcomodClass: EcomodClass,
      isEmptyCup: boolean = false,
    ) => {
      // Find the corresponding ecomod class
      const ecomodClass = ecomodClasses?.find(
        (cls) => cls.code === materialCode,
      );

      rows.push({
        id: findMatchingSecondaryIdFromReportData(
          reportData,
          materialCode,
          ownerEcomodClass.code as string,
          businessUse,
          reusableClass,
          packagedAbroad,
          isEmptyCup,
        ),
        ecoModulationClass: ownerEcomodClass,
        businessUse,
        reusableClass,
        packagedAbroad,
        value: 0,
        secMaterial: ecomodClass as EcomodClass,
        secValue: value,
        secValueType: 1,
        isEmptyCup: isEmptyCup,
      });
    };

    // Process Consumer Packaging
    if (data.consumerPackaging) {
      // Single Use
      if (data.consumerPackaging.singleUse) {
        // Packaged in Finland
        processMaterialData(
          data.consumerPackaging.singleUse.packagedInFinland || {},
          false, // businessUse = false for consumer
          ReusableClass.NONE,
          false, // packagedAbroad = false for packaged in Finland
        );

        // Imported Packages
        processMaterialData(
          data.consumerPackaging.singleUse.importedPackages || {},
          false,
          ReusableClass.NONE,
          true, // packagedAbroad = true for imported
        );

        // Multi-material - Packaged in Finland
        processMaterialData(
          data.consumerPackaging.singleUse.multiMaterial?.packagedInFinland ||
            {},
          false,
          ReusableClass.NONE,
          false,
        );

        // Multi-material - Imported Packages
        processMaterialData(
          data.consumerPackaging.singleUse.multiMaterial?.importedPackages ||
            {},
          false,
          ReusableClass.NONE,
          true,
        );
      }

      // Reusable
      if (data.consumerPackaging.reusable) {
        // First Usage
        processMaterialData(
          data.consumerPackaging.reusable.firstUsage || {},
          false,
          ReusableClass.FIRST_USE,
          false,
        );

        // Imported Packages
        processMaterialData(
          data.consumerPackaging.reusable.importedPackages || {},
          false,
          ReusableClass.FIRST_USE,
          true,
        );

        // Reused
        processMaterialData(
          data.consumerPackaging.reusable.reused || {},
          false,
          ReusableClass.REUSABLE,
          false,
        );

        // Multi-material - First Usage
        processMaterialData(
          data.consumerPackaging.reusable.multiMaterial?.firstUsage || {},
          false,
          ReusableClass.FIRST_USE,
          false,
        );

        // Multi-material - Imported Packages
        processMaterialData(
          data.consumerPackaging.reusable.multiMaterial?.importedPackages || {},
          false,
          ReusableClass.FIRST_USE,
          true,
        );

        // Multi-material - Reused
        processMaterialData(
          data.consumerPackaging.reusable.multiMaterial?.reused || {},
          false,
          ReusableClass.REUSABLE,
          false,
        );
      }

      // Process secondary materials for consumer packaging
      if (
        data.consumerPackaging.secondaryMaterials &&
        data.consumerPackaging.secondaryMaterials.length > 0
      ) {
        data.consumerPackaging.secondaryMaterials.forEach((secMat) => {
          const materialCode = secMat.materialCode;
          const ownerEcomodClass = ecomodClasses?.find(
            (cls) => cls.code === secMat.ecoModulationClassCode,
          );

          // Single Use
          if (secMat.singleUse) {
            // Packaged in Finland
            if (
              secMat.singleUse.packagedInFinland &&
              parseFloat(secMat.singleUse.packagedInFinland) > 0
            ) {
              processSecondaryMaterials(
                materialCode,
                false,
                ReusableClass.NONE,
                false,
                parseFloat(secMat.singleUse.packagedInFinland),
                ownerEcomodClass as EcomodClass,
              );
            }

            // Imported Packages
            if (
              secMat.singleUse.importedPackages &&
              parseFloat(secMat.singleUse.importedPackages) > 0
            ) {
              processSecondaryMaterials(
                materialCode,
                false,
                ReusableClass.NONE,
                true,
                parseFloat(secMat.singleUse.importedPackages),
                ownerEcomodClass as EcomodClass,
              );
            }
          }

          // Reusable
          if (secMat.reusable) {
            // First Usage
            if (
              secMat.reusable.firstUsage &&
              parseFloat(secMat.reusable.firstUsage) > 0
            ) {
              processSecondaryMaterials(
                materialCode,
                false,
                ReusableClass.FIRST_USE,
                false,
                parseFloat(secMat.reusable.firstUsage),
                ownerEcomodClass as EcomodClass,
              );
            }

            // Imported Packages
            if (
              secMat.reusable.importedPackages &&
              parseFloat(secMat.reusable.importedPackages) > 0
            ) {
              processSecondaryMaterials(
                materialCode,
                false,
                ReusableClass.FIRST_USE,
                true,
                parseFloat(secMat.reusable.importedPackages),
                ownerEcomodClass as EcomodClass,
              );
            }

            // Reused
            if (
              secMat.reusable.reused &&
              parseFloat(secMat.reusable.reused) > 0
            ) {
              processSecondaryMaterials(
                materialCode,
                false,
                ReusableClass.REUSABLE,
                false,
                parseFloat(secMat.reusable.reused),
                ownerEcomodClass as EcomodClass,
              );
            }
          }
        });
      }
    }

    // Process Business Packaging
    if (data.businessPackaging) {
      // Single Use
      if (data.businessPackaging.singleUse) {
        // Packaged in Finland
        processMaterialData(
          data.businessPackaging.singleUse.packagedInFinland || {},
          true, // businessUse = true for business
          ReusableClass.NONE,
          false,
        );

        // Imported Packages
        processMaterialData(
          data.businessPackaging.singleUse.importedPackages || {},
          true,
          ReusableClass.NONE,
          true,
        );

        // Multi-material - Packaged in Finland
        processMaterialData(
          data.businessPackaging.singleUse.multiMaterial?.packagedInFinland ||
            {},
          true,
          ReusableClass.NONE,
          false,
        );

        // Multi-material - Imported Packages
        processMaterialData(
          data.businessPackaging.singleUse.multiMaterial?.importedPackages ||
            {},
          true,
          ReusableClass.NONE,
          true,
        );
      }

      // Reusable
      if (data.businessPackaging.reusable) {
        // First Usage
        processMaterialData(
          data.businessPackaging.reusable.firstUsage || {},
          true,
          ReusableClass.FIRST_USE,
          false,
        );

        // Imported Packages
        processMaterialData(
          data.businessPackaging.reusable.importedPackages || {},
          true,
          ReusableClass.FIRST_USE,
          true,
        );

        // Reused
        processMaterialData(
          data.businessPackaging.reusable.reused || {},
          true,
          ReusableClass.REUSABLE,
          false,
        );

        // Multi-material - First Usage
        processMaterialData(
          data.businessPackaging.reusable.multiMaterial?.firstUsage || {},
          true,
          ReusableClass.FIRST_USE,
          false,
        );

        // Multi-material - Imported Packages
        processMaterialData(
          data.businessPackaging.reusable.multiMaterial?.importedPackages || {},
          true,
          ReusableClass.FIRST_USE,
          true,
        );

        // Multi-material - Reused
        processMaterialData(
          data.businessPackaging.reusable.multiMaterial?.reused || {},
          true,
          ReusableClass.REUSABLE,
          false,
        );
      }

      // Process secondary materials for business packaging
      if (
        data.businessPackaging.secondaryMaterials &&
        data.businessPackaging.secondaryMaterials.length > 0
      ) {
        data.businessPackaging.secondaryMaterials.forEach((secMat) => {
          const materialCode = secMat.materialCode;

          const ownerEcomodClass = ecomodClasses?.find(
            (cls) => cls.code === secMat.ecoModulationClassCode,
          );

          // Single Use
          if (secMat.singleUse) {
            // Packaged in Finland
            if (
              secMat.singleUse.packagedInFinland &&
              parseFloat(secMat.singleUse.packagedInFinland) > 0
            ) {
              processSecondaryMaterials(
                materialCode,
                true,
                ReusableClass.NONE,
                false,
                parseFloat(secMat.singleUse.packagedInFinland),
                ownerEcomodClass as EcomodClass,
              );
            }

            // Imported Packages
            if (
              secMat.singleUse.importedPackages &&
              parseFloat(secMat.singleUse.importedPackages) > 0
            ) {
              processSecondaryMaterials(
                materialCode,
                true,
                ReusableClass.NONE,
                true,
                parseFloat(secMat.singleUse.importedPackages),
                ownerEcomodClass as EcomodClass,
              );
            }
          }

          // Reusable
          if (secMat.reusable) {
            // First Usage
            if (
              secMat.reusable.firstUsage &&
              parseFloat(secMat.reusable.firstUsage) > 0
            ) {
              processSecondaryMaterials(
                materialCode,
                true,
                ReusableClass.FIRST_USE,
                false,
                parseFloat(secMat.reusable.firstUsage),
                ownerEcomodClass as EcomodClass,
              );
            }

            // Imported Packages
            if (
              secMat.reusable.importedPackages &&
              parseFloat(secMat.reusable.importedPackages) > 0
            ) {
              processSecondaryMaterials(
                materialCode,
                true,
                ReusableClass.FIRST_USE,
                true,
                parseFloat(secMat.reusable.importedPackages),
                ownerEcomodClass as EcomodClass,
              );
            }

            // Reused
            if (
              secMat.reusable.reused &&
              parseFloat(secMat.reusable.reused) > 0
            ) {
              processSecondaryMaterials(
                materialCode,
                true,
                ReusableClass.REUSABLE,
                false,
                parseFloat(secMat.reusable.reused),
                ownerEcomodClass as EcomodClass,
              );
            }
          }
        });
      }
    }

    // Process Empty Cups
    if (data.consumerCups) {
      // Single material - Produced in Finland
      processMaterialData(
        data.consumerCups.producedInFinland || {},
        false,
        ReusableClass.NONE,
        false,
        true, // isEmptyCup = true
      );

      // Single material - Imported Cups
      processMaterialData(
        data.consumerCups.importedCups || {},
        false,
        ReusableClass.NONE,
        true,
        true,
      );

      // Multi-material - Produced in Finland
      processMaterialData(
        data.consumerCups.multiMaterial?.producedInFinland || {},
        false,
        ReusableClass.NONE,
        false,
        true,
      );

      // Multi-material - Imported Cups
      processMaterialData(
        data.consumerCups.multiMaterial?.importedCups || {},
        false,
        ReusableClass.NONE,
        true,
        true,
      );

      // Process secondary materials for consumer cups
      if (
        data.consumerCups.secondaryMaterials &&
        data.consumerCups.secondaryMaterials.length > 0
      ) {
        data.consumerCups.secondaryMaterials.forEach((secMat) => {
          const materialCode = secMat.materialCode;
          const ownerEcomodClass = ecomodClasses?.find(
            (cls) => cls.code === secMat.ecoModulationClassCode,
          );

          // Produced in Finland
          if (
            secMat.producedInFinland &&
            parseFloat(secMat.producedInFinland) > 0
          ) {
            processSecondaryMaterials(
              materialCode,
              false,
              ReusableClass.NONE,
              false,
              parseFloat(secMat.producedInFinland),
              ownerEcomodClass as EcomodClass,
              true, // isEmptyCup = true
            );
          }

          // Imported Cups
          if (secMat.importedCups && parseFloat(secMat.importedCups) > 0) {
            processSecondaryMaterials(
              materialCode,
              false,
              ReusableClass.NONE,
              true,
              parseFloat(secMat.importedCups),
              ownerEcomodClass as EcomodClass,
              true, // isEmptyCup = true
            );
          }
        });
      }
    }

    if (data.businessCups) {
      // Single material - Produced in Finland
      processMaterialData(
        data.businessCups.producedInFinland || {},
        true,
        ReusableClass.NONE,
        false,
        true,
      );

      // Single material - Imported Cups
      processMaterialData(
        data.businessCups.importedCups || {},
        true,
        ReusableClass.NONE,
        true,
        true,
      );

      // Multi-material - Produced in Finland
      processMaterialData(
        data.businessCups.multiMaterial?.producedInFinland || {},
        true,
        ReusableClass.NONE,
        false,
        true,
      );

      // Multi-material - Imported Cups
      processMaterialData(
        data.businessCups.multiMaterial?.importedCups || {},
        true,
        ReusableClass.NONE,
        true,
        true,
      );

      // Process secondary materials for business cups
      if (
        data.businessCups.secondaryMaterials &&
        data.businessCups.secondaryMaterials.length > 0
      ) {
        data.businessCups.secondaryMaterials.forEach((secMat) => {
          const materialCode = secMat.materialCode;
          const ownerEcomodClass = ecomodClasses?.find(
            (cls) => cls.code === secMat.ecoModulationClassCode,
          );

          // Produced in Finland
          if (
            secMat.producedInFinland &&
            parseFloat(secMat.producedInFinland) > 0
          ) {
            processSecondaryMaterials(
              materialCode,
              true,
              ReusableClass.NONE,
              false,
              parseFloat(secMat.producedInFinland),
              ownerEcomodClass as EcomodClass,
              true, // isEmptyCup = true
            );
          }

          // Imported Cups
          if (secMat.importedCups && parseFloat(secMat.importedCups) > 0) {
            processSecondaryMaterials(
              materialCode,
              true,
              ReusableClass.NONE,
              true,
              parseFloat(secMat.importedCups),
              ownerEcomodClass as EcomodClass,
              true, // isEmptyCup = true
            );
          }
        });
      }
    }

    return rows;
  };

  const createReportRowFromFormData = async (
    data: MaterialFormDataWithSpecificCodes,
  ): Promise<ReportRow[] | void> => {
    if (reportData?.rows && reportData.rows.length > 0) {
      const firstRow = reportData.rows[0];

      const rowPayload: UpdateReportRowPayload = {
        id: firstRow.id as number,
        reportId: reportId as number,
        ean: firstRow.ean,
        name: firstRow.name,
        amount: firstRow.amount,
        active: firstRow.active,
        values: firstRow
          ? firstRow.values
              .filter((val) => !!val.supClass)
              .concat(generateRowFromData(data)) // Values from SUP class form will be preserved
          : generateRowFromData(data),
      };

      const result = await updateReportRowData(rowPayload);
      return [result];
    } else {
      const rowPayload: CreateReportRowPayload = {
        reportId: reportId as number,
        ean: "",
        name: "Generic Material Row",
        amount: 1,
        active: true,
        values: generateRowFromData(data),
      };

      const result = await createReportRowData(rowPayload);
      return [result];
    }
  };

  // validation ensuring that if any of the multi-material packaging for an ecomod class is filled,
  // then at least one of the corresponding secondary materials fields has to be filled
  // Also validates that secondary materials cannot exist without corresponding primary materials
  const validateSecondaryMaterials = (): {
    isValid: boolean;
    errorFields: { fieldPath: string; message: string }[];
  } => {
    let valid = true;
    let errorMessage = "";
    const errorFields: { fieldPath: string; message: string }[] = [];

    const packageTypes = ["consumer", "business"];

    (packageTypes as ("consumer" | "business")[]).forEach((packageType) => {
      (ecomodClasses || []).forEach((cls) => {
        const ecomodClassCode = cls.code as string;

        // Skip validation for consumer packaging if the material is business-only
        if (businessOnlyMultimaterialPackageCodes.includes(ecomodClassCode)) {
          return;
        }

        const singleUsePackagedInFinland = watch(
          `${packageType}Packaging.singleUse.multiMaterial.packagedInFinland.${ecomodClassCode}`,
        );
        const singleUseImportedPackages = watch(
          `${packageType}Packaging.singleUse.multiMaterial.importedPackages.${ecomodClassCode}`,
        );
        const reusableFirstUsage = watch(
          `${packageType}Packaging.reusable.multiMaterial.firstUsage.${ecomodClassCode}`,
        );
        const reusableImportedPackages = watch(
          `${packageType}Packaging.reusable.multiMaterial.importedPackages.${ecomodClassCode}`,
        );
        const reusableReused = watch(
          `${packageType}Packaging.reusable.multiMaterial.reused.${ecomodClassCode}`,
        );

        const secMats: SecondaryMaterials[] =
          watch(`${packageType}Packaging.secondaryMaterials`) || [];

        // Check if primary material has value, then secondary must have value
        if (singleUsePackagedInFinland) {
          // check if secondaryMaterials has an entry for this ecomodClassCode. If not, set valid to false
          const matchingSecMat = secMats.find(
            (sm) => sm.ecoModulationClassCode === ecomodClassCode,
          );

          if (!matchingSecMat) {
            valid = false;
            errorMessage = t("msg-fill-secondary-materials-for-multi-material");
            errorFields.push({
              fieldPath: `${packageType}Packaging.singleUse.multiMaterial.packagedInFinland.${ecomodClassCode}`,
              message: t("secondary-material-validation-error"),
            });
          } else {
            // check if any of the values are set
            const hasValue =
              matchingSecMat.singleUse.packagedInFinland !== null &&
              matchingSecMat.singleUse.packagedInFinland !== "";

            if (!hasValue) {
              valid = false;
              errorMessage = t(
                "msg-fill-secondary-materials-for-multi-material",
              );
              errorFields.push({
                fieldPath: `${packageType}Packaging.singleUse.multiMaterial.packagedInFinland.${ecomodClassCode}`,
                message: t("secondary-material-validation-error"),
              });
            }
          }
        }

        if (singleUseImportedPackages) {
          const matchingSecMat = secMats.find(
            (sm) => sm.ecoModulationClassCode === ecomodClassCode,
          );

          if (!matchingSecMat) {
            valid = false;
            errorMessage = t("msg-fill-secondary-materials-for-multi-material");
            errorFields.push({
              fieldPath: `${packageType}Packaging.singleUse.multiMaterial.importedPackages.${ecomodClassCode}`,
              message: t("secondary-material-validation-error"),
            });
          } else {
            const hasValue =
              matchingSecMat.singleUse.importedPackages !== null &&
              matchingSecMat.singleUse.importedPackages !== "";

            if (!hasValue) {
              valid = false;
              errorMessage = t(
                "msg-fill-secondary-materials-for-multi-material",
              );
              errorFields.push({
                fieldPath: `${packageType}Packaging.singleUse.multiMaterial.importedPackages.${ecomodClassCode}`,
                message: t("secondary-material-validation-error"),
              });
            }
          }
        }

        if (reusableFirstUsage) {
          const matchingSecMat = secMats.find(
            (sm) => sm.ecoModulationClassCode === ecomodClassCode,
          );

          if (!matchingSecMat) {
            valid = false;
            errorMessage = t("msg-fill-secondary-materials-for-multi-material");
            errorFields.push({
              fieldPath: `${packageType}Packaging.reusable.multiMaterial.firstUsage.${ecomodClassCode}`,
              message: t("secondary-material-validation-error"),
            });
          } else {
            const hasValue =
              matchingSecMat.reusable.firstUsage !== null &&
              matchingSecMat.reusable.firstUsage !== "";

            if (!hasValue) {
              valid = false;
              errorMessage = t(
                "msg-fill-secondary-materials-for-multi-material",
              );
              errorFields.push({
                fieldPath: `${packageType}Packaging.reusable.multiMaterial.firstUsage.${ecomodClassCode}`,
                message: t("secondary-material-validation-error"),
              });
            }
          }
        }

        if (reusableImportedPackages) {
          const matchingSecMat = secMats.find(
            (sm) => sm.ecoModulationClassCode === ecomodClassCode,
          );

          if (!matchingSecMat) {
            valid = false;
            errorMessage = t("msg-fill-secondary-materials-for-multi-material");
            errorFields.push({
              fieldPath: `${packageType}Packaging.reusable.multiMaterial.importedPackages.${ecomodClassCode}`,
              message: t("secondary-material-validation-error"),
            });
          } else {
            const hasValue =
              matchingSecMat.reusable.importedPackages !== null &&
              matchingSecMat.reusable.importedPackages !== "";

            if (!hasValue) {
              valid = false;
              errorMessage = t(
                "msg-fill-secondary-materials-for-multi-material",
              );
              errorFields.push({
                fieldPath: `${packageType}Packaging.reusable.multiMaterial.importedPackages.${ecomodClassCode}`,
                message: t("secondary-material-validation-error"),
              });
            }
          }
        }

        if (reusableReused) {
          const matchingSecMat = secMats.find(
            (sm) => sm.ecoModulationClassCode === ecomodClassCode,
          );

          if (!matchingSecMat) {
            valid = false;
            errorMessage = t("msg-fill-secondary-materials-for-multi-material");
            errorFields.push({
              fieldPath: `${packageType}Packaging.reusable.multiMaterial.reused.${ecomodClassCode}`,
              message: t("secondary-material-validation-error"),
            });
          } else {
            const hasValue =
              matchingSecMat.reusable.reused !== null &&
              matchingSecMat.reusable.reused !== "";

            if (!hasValue) {
              valid = false;
              errorMessage = t(
                "msg-fill-secondary-materials-for-multi-material",
              );
              errorFields.push({
                fieldPath: `${packageType}Packaging.reusable.multiMaterial.reused.${ecomodClassCode}`,
                message: t("secondary-material-validation-error"),
              });
            }
          }
        }

        // Check reverse: if secondary material has value but primary material doesn't
        secMats.forEach((secMat) => {
          if (secMat.ecoModulationClassCode !== ecomodClassCode) {
            return;
          }

          // Check singleUse.packagedInFinland
          if (
            secMat.singleUse?.packagedInFinland &&
            secMat.singleUse.packagedInFinland !== ""
          ) {
            if (
              !singleUsePackagedInFinland ||
              singleUsePackagedInFinland === ""
            ) {
              valid = false;
              errorMessage = t("msg-cannot-have-secondary-without-primary");
              errorFields.push({
                fieldPath: `${packageType}Packaging.singleUse.multiMaterial.packagedInFinland.${ecomodClassCode}`,
                message: t("main-material-validation-error"),
              });
            }
          }

          // Check singleUse.importedPackages
          if (
            secMat.singleUse?.importedPackages &&
            secMat.singleUse.importedPackages !== ""
          ) {
            if (
              !singleUseImportedPackages ||
              singleUseImportedPackages === ""
            ) {
              valid = false;
              errorMessage = t("msg-cannot-have-secondary-without-primary");
              errorFields.push({
                fieldPath: `${packageType}Packaging.singleUse.multiMaterial.importedPackages.${ecomodClassCode}`,
                message: t("main-material-validation-error"),
              });
            }
          }

          // Check reusable.firstUsage
          if (
            secMat.reusable?.firstUsage &&
            secMat.reusable.firstUsage !== ""
          ) {
            if (!reusableFirstUsage || reusableFirstUsage === "") {
              valid = false;
              errorMessage = t("msg-cannot-have-secondary-without-primary");
              errorFields.push({
                fieldPath: `${packageType}Packaging.reusable.multiMaterial.firstUsage.${ecomodClassCode}`,
                message: t("main-material-validation-error"),
              });
            }
          }

          // Check reusable.importedPackages
          if (
            secMat.reusable?.importedPackages &&
            secMat.reusable.importedPackages !== ""
          ) {
            if (!reusableImportedPackages || reusableImportedPackages === "") {
              valid = false;
              errorMessage = t("msg-cannot-have-secondary-without-primary");
              errorFields.push({
                fieldPath: `${packageType}Packaging.reusable.multiMaterial.importedPackages.${ecomodClassCode}`,
                message: t("main-material-validation-error"),
              });
            }
          }

          // Check reusable.reused
          if (secMat.reusable?.reused && secMat.reusable.reused !== "") {
            if (!reusableReused || reusableReused === "") {
              valid = false;
              errorMessage = t("msg-cannot-have-secondary-without-primary");
              errorFields.push({
                fieldPath: `${packageType}Packaging.reusable.multiMaterial.reused.${ecomodClassCode}`,
                message: t("main-material-validation-error"),
              });
            }
          }
        });
      });
    });

    if (!valid && errorMessage) {
      toast?.current?.show({
        severity: "warn",
        detail: errorMessage,
        life: 3000,
      });
    }
    return { isValid: valid, errorFields };
  };

  const handleUpdateReport = async (
    data: MaterialFormDataWithSpecificCodes,
  ) => {
    if (!reportId) return;

    const validation = validateSecondaryMaterials();
    setValidationErrorFields(validation.errorFields);

    if (!validation.isValid) {
      return;
    }

    // Clear validation errors if validation passes
    setValidationErrorFields([]);

    await createReportRowFromFormData(data);

    handleNextStep();
  };

  const handleNextStepClick = () => {
    // Submit the form data
    handleSubmit(handleUpdateReport)();
  };

  const handleNextMaterial = () => {
    if (activeTabIndex < tabs.length - 1) {
      setActiveTabIndex(activeTabIndex + 1);
    }
  };

  const handleSaveDraft = async () => {
    if (!reportId) return;

    const validation = validateSecondaryMaterials();
    setValidationErrorFields(validation.errorFields);

    if (!validation.isValid) {
      // Don't save if validation fails
      throw new Error(
        "Validation failed - cannot save draft with invalid data",
      );
    }

    // Clear validation errors if validation passes
    setValidationErrorFields([]);

    await createReportRowFromFormData(getValues());
  };

  // Expose save draft function to parent through ref
  useEffect(() => {
    if (saveDraftRef) {
      saveDraftRef.current = handleSaveDraft;
    }
  }, [saveDraftRef, handleSaveDraft]);

  useEffect(() => {
    if (ecomodClasses) {
      const filteredTabs = ecomodClasses
        .filter((cls) => {
          return isInteger(cls.code as string);
        })
        .map((cls) => ({
          code: cls.code as string,
          label: getSecondaryClassLabelFromAkeneoObject(locale, cls) as string,
          tooltip: cls.code === "6" ? "input-instructions.material-tab-6" : "",
        }));

      /* Extra tabs for paper cups because why not */
      filteredTabs.push({
        code: "x_1",
        label: t("label-paper-cups-paper"),
        tooltip: "",
      });
      filteredTabs.push({
        code: "x_2",
        label: t("label-paper-cups-plastic"),
        tooltip: "",
      });
      setTabs(filteredTabs);
    }
  }, [ecomodClasses, locale, t]);

  const businessPackaging = watch("businessPackaging");
  const consumerPackaging = watch("consumerPackaging");

  return (
    <LoadingContainer
      status={isLoading ? HttpRequestStatus.LOADING : HttpRequestStatus.SUCCESS}
    >
      <PaddedContainer>
        <form>
          <RelativeTabView
            activeIndex={activeTabIndex}
            onTabChange={(e) => setActiveTabIndex(e.index)}
          >
            {tabs?.map((tab) => (
              <TabPanel
                key={tab.code}
                header={
                  <TabHeaderWithError $hasError={tabHasErrors(tab.code)}>
                    {tab.tooltip ? (
                      <InputInstructionsHover translationKey={tab.tooltip}>
                        <span>{tab.label}</span>
                      </InputInstructionsHover>
                    ) : (
                      tab.label
                    )}
                  </TabHeaderWithError>
                }
              >
                {tab.code === "x_1" || tab.code === "x_2" ? (
                  <EmptyCupTab
                    allEcomodClasses={ecomodClasses || []}
                    ecomodClasses={
                      ecomodClasses?.filter((cls) =>
                        cls.code?.startsWith(tab.code.replace("x_", "")),
                      ) || []
                    }
                    control={control}
                    watch={watch}
                  />
                ) : (
                  <MaterialTab
                    ecomodClasses={
                      ecomodClasses?.filter((cls) =>
                        cls.code?.startsWith(tab.code),
                      ) || []
                    }
                    control={control}
                    watch={watch}
                    allEcomodClasses={ecomodClasses || []}
                    validationErrorFields={validationErrorFields}
                    tabCode={tab.code}
                  />
                )}
              </TabPanel>
            ))}
          </RelativeTabView>
        </form>
      </PaddedContainer>
      <ReportFooter
        saveDraft={handleSaveDraft}
        handleNextStep={handleNextStepClick}
        nextStepLabel={t("cmd-to-sup-reporting")}
        proceedDisabled={!businessPackaging && !consumerPackaging}
        isLoading={isCreatingReportRow || isUpdatingReportRow}
        secondaryAction={
          <Button
            label={t("cmd-next-material")}
            onClick={(e) => {
              e.stopPropagation();
              handleNextMaterial();
            }}
            disabled={activeTabIndex >= tabs.length - 1}
          />
        }
      />
    </LoadingContainer>
  );
};

export default BasicReportForm;
